import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isAdmin } from '@/auth'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card } from '@/shared/components/atoms/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { type ApprovalDTO, type ListApprovalParams, requestCenterApi } from '../api/request-center.api'
import { requestCenterKeys } from '../hooks/query-keys'
import { SubmitterRepliedPill } from './_infoRequestNotice'

const PAGE_SIZE = 20
/** Concurrency cap for client-side fan-out on bulk actions. */
const BULK_CONCURRENCY = 5

const PRIORITY_OPTIONS = ['critical', 'high', 'normal', 'low'] as const
const SORT_OPTIONS: Array<{ value: NonNullable<ListApprovalParams['sort']>; label: string }> = [
	{ value: 'due', label: 'Due date' },
	{ value: 'priority', label: 'Priority' },
	{ value: 'age', label: 'Age' },
	{ value: 'type', label: 'Type' },
]

/**
 * All inbox state that affects the URL / query. Kept together so `onClear`
 * can reset atomically and the `useMemo(params)` dependency is one object.
 */
interface InboxFilters {
	scope: 'mine' | 'all'
	typeId: string
	priorities: Set<string>
	dueBefore: string
	dueAfter: string
	approverId: string
	submitter: string
	search: string
	status: 'pending' | 'any'
	sort: NonNullable<ListApprovalParams['sort']>
	order: NonNullable<ListApprovalParams['order']>
}

const DEFAULT_FILTERS: InboxFilters = {
	scope: 'mine',
	typeId: '',
	priorities: new Set(),
	dueBefore: '',
	dueAfter: '',
	approverId: '',
	submitter: '',
	search: '',
	status: 'pending',
	sort: 'due',
	order: 'asc',
}

/**
 * `/requests-center/approvals` — the approval inbox.
 *
 * Default view shows approvals assigned to the caller (`filters[scope]=mine`).
 * Admins can toggle to `all` to see every pending approval; the list is
 * partitioned into "Assigned to me" + "Other approvers" using the per-row
 * `viewerMatch` hint from the backend. Pagination is global across both
 * groups. Phase D adds a filter bar (type / priority / due range / submitter
 * / approverId / free-text search / status) and sort controls.
 *
 * Deferred (Phase E polish): approver + submitter employee pickers
 * (autocomplete) — currently accept raw UUIDs.
 */
/**
 * Summary emitted when a bulk action finishes. `failures` carries the ids
 * the fan-out couldn't complete + the best-effort reason from the server.
 * Rendered as a banner until the user dismisses it.
 */
interface BulkSummary {
	action: 'approve' | 'reject'
	total: number
	succeeded: number
	failures: Array<{ id: string; message: string }>
}

interface BulkModalState {
	action: 'approve' | 'reject'
	targetIds: string[]
}

export function ApprovalsInboxPage() {
	const { user } = useAuth()
	const admin = isAdmin(user?.role)
	const queryClient = useQueryClient()

	const [page, setPage] = useState(0)
	// Allow deep links to land on the admin-wide view via `?scope=all`. Non-
	// admin callers are silently clamped to `mine` regardless of the URL — the
	// backend enforces the same clamp, this is just for UI consistency.
	const [searchParams] = useSearchParams()
	const initialScope: 'mine' | 'all' = admin && searchParams.get('scope') === 'all' ? 'all' : 'mine'
	const [filters, setFilters] = useState<InboxFilters>({ ...DEFAULT_FILTERS, scope: initialScope })
	const [selected, setSelected] = useState<Set<string>>(new Set())
	const [bulkModal, setBulkModal] = useState<BulkModalState | null>(null)
	const [bulkRunning, setBulkRunning] = useState(false)
	const [bulkSummary, setBulkSummary] = useState<BulkSummary | null>(null)

	// `setFilters` helpers — each one resets pagination so results are aligned.
	const updateFilter = <K extends keyof InboxFilters>(key: K, value: InboxFilters[K]) => {
		setPage(0)
		setFilters((prev) => ({ ...prev, [key]: value }))
	}

	const params: ListApprovalParams = useMemo(
		() => ({
			scope: admin && filters.scope === 'all' ? 'all' : undefined,
			type: filters.typeId || undefined,
			priority: filters.priorities.size > 0 ? Array.from(filters.priorities).join(',') : undefined,
			dueBefore: filters.dueBefore || undefined,
			dueAfter: filters.dueAfter || undefined,
			approverId: filters.approverId || undefined,
			submitter: filters.submitter || undefined,
			search: filters.search || undefined,
			status: filters.status === 'any' ? 'any' : undefined,
			sort: filters.sort,
			order: filters.order,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
		}),
		[admin, filters, page],
	)

	const hasActiveFilters = Boolean(
		params.type ||
			params.priority ||
			params.dueBefore ||
			params.dueAfter ||
			params.approverId ||
			params.submitter ||
			params.search ||
			params.status === 'any',
	)

	const { data: typesData } = useQuery({
		queryKey: requestCenterKeys.types.forFilter(),
		queryFn: () => requestCenterApi.listRequestTypes({ limit: 100 }),
		staleTime: 5 * 60 * 1000,
	})

	const { data, isLoading, error } = useQuery({
		queryKey: requestCenterKeys.approvals.inbox(params),
		queryFn: () => requestCenterApi.listApprovals(params),
	})

	const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

	// Selection is per-page: anything that changes the visible row set should
	// wipe it (pagination, filters, scope, sort — all flow through `params`).
	useEffect(() => {
		setSelected(new Set())
	}, [params])

	const clearFilters = () => {
		setPage(0)
		setFilters((prev) => ({ ...DEFAULT_FILTERS, scope: prev.scope, sort: prev.sort, order: prev.order }))
	}

	const toggleSelected = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const pageIds = useMemo(() => (data ? data.data.map((r) => r.id) : []), [data])
	const selectableIds = useMemo(
		() => (data ? data.data.filter((r) => r.status === 'pending').map((r) => r.id) : []),
		[data],
	)
	const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))
	const toggleSelectAll = () => {
		setSelected((prev) => {
			if (allSelected) {
				// De-select everything on the current page (both pending + decided).
				const next = new Set(prev)
				for (const id of pageIds) next.delete(id)
				return next
			}
			// Only pending rows are actionable; don't silently add decided rows.
			return new Set([...prev, ...selectableIds])
		})
	}

	const runBulk = async (action: 'approve' | 'reject', ids: string[], comment: string) => {
		setBulkRunning(true)
		const results: BulkSummary = { action, total: ids.length, succeeded: 0, failures: [] }
		// Process in chunks of BULK_CONCURRENCY to avoid hammering the API.
		for (let i = 0; i < ids.length; i += BULK_CONCURRENCY) {
			const chunk = ids.slice(i, i + BULK_CONCURRENCY)
			const settled = await Promise.allSettled(
				chunk.map((id) =>
					action === 'approve'
						? requestCenterApi.approveApproval(id, comment || undefined)
						: requestCenterApi.rejectApproval(id, comment),
				),
			)
			settled.forEach((r, idx) => {
				if (r.status === 'fulfilled') {
					results.succeeded += 1
				} else {
					// `PromiseRejectedResult.reason` is typed `any` upstream — narrow
					// to `unknown` first so the shape-check below is sound under
					// `no-unsafe-assignment`.
					const reason: unknown = r.reason
					let message = 'Unknown error'
					if (reason && typeof reason === 'object') {
						const axiosLike = reason as { response?: { data?: { error?: { message?: string } } }; message?: string }
						message = axiosLike.response?.data?.error?.message ?? axiosLike.message ?? message
					}
					results.failures.push({ id: chunk[idx], message })
				}
			})
		}
		setBulkRunning(false)
		setBulkSummary(results)
		setSelected(new Set())
		setBulkModal(null)
		await queryClient.invalidateQueries({ queryKey: requestCenterKeys.approvals.all })
	}

	return (
		<div className="mx-auto max-w-5xl space-y-4">
			<header className="space-y-1">
				<h1 className="text-2xl font-semibold tracking-tight">Approvals</h1>
				<p className="text-sm text-muted-foreground">
					{admin && filters.scope === 'all'
						? 'Every pending approval across the organisation. Yours are listed first.'
						: 'Approvals currently assigned to you.'}
				</p>
			</header>

			{admin ? <ScopeTabs scope={filters.scope} onChange={(next) => updateFilter('scope', next)} /> : null}

			<FiltersBar
				filters={filters}
				onFilterChange={updateFilter}
				onClear={clearFilters}
				hasActiveFilters={hasActiveFilters}
				typeOptions={typesData ?? []}
			/>

			{bulkSummary ? <BulkSummaryBanner summary={bulkSummary} onDismiss={() => setBulkSummary(null)} /> : null}

			{data && data.data.length > 0 ? (
				<BulkToolbar
					selectedCount={selected.size}
					selectableCount={selectableIds.length}
					allSelected={allSelected}
					onToggleAll={toggleSelectAll}
					onApprove={() => setBulkModal({ action: 'approve', targetIds: Array.from(selected) })}
					onReject={() => setBulkModal({ action: 'reject', targetIds: Array.from(selected) })}
					onClearSelection={() => setSelected(new Set())}
				/>
			) : null}

			{isLoading ? (
				<ListSkeleton />
			) : error ? (
				<div className="text-sm text-destructive">Failed to load approvals.</div>
			) : !data || data.data.length === 0 ? (
				<EmptyState
					variant={hasActiveFilters ? 'filtered' : 'empty'}
					onClear={hasActiveFilters ? clearFilters : undefined}
				/>
			) : (
				<>
					{admin && filters.scope === 'all' ? (
						<GroupedList rows={data.data} selected={selected} onToggleSelected={toggleSelected} />
					) : (
						<ul className="space-y-2" data-testid="approvals-inbox-list">
							{[...data.data]
								.sort((a, b) => priorityForAnswered(b) - priorityForAnswered(a))
								.map((a) => (
									<ApprovalRow
										key={a.id}
										approval={a}
										isAdmin={admin}
										selected={selected.has(a.id)}
										onToggleSelected={toggleSelected}
									/>
								))}
						</ul>
					)}
					{totalPages > 1 ? (
						<nav className="flex items-center justify-between" aria-label="Pagination">
							<p className="text-xs text-muted-foreground">
								Page {page + 1} of {totalPages} · {data.total} total
							</p>
							<div className="flex gap-2">
								<Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
									Previous
								</Button>
								<Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages}>
									Next
								</Button>
							</div>
						</nav>
					) : null}
				</>
			)}

			{bulkModal ? (
				<BulkActionModal
					action={bulkModal.action}
					targetCount={bulkModal.targetIds.length}
					running={bulkRunning}
					onCancel={() => setBulkModal(null)}
					onConfirm={(comment) => {
						void runBulk(bulkModal.action, bulkModal.targetIds, comment)
					}}
				/>
			) : null}
		</div>
	)
}

/**
 * Filter bar for the approvals inbox. Controlled by the parent page via
 * `filters` + `onFilterChange`. Renders the filter + sort controls only —
 * admin scope (`mine` vs `all`) lives on the view-level `<ScopeTabs>` above.
 *
 * Kept intentionally plain (no collapsibles / popovers) — every control is
 * inline. Phase E can add URL-serialisation / deep-linking / pickers if the
 * surface grows.
 */
function FiltersBar({
	filters,
	onFilterChange,
	onClear,
	hasActiveFilters,
	typeOptions,
}: {
	filters: InboxFilters
	onFilterChange: <K extends keyof InboxFilters>(key: K, value: InboxFilters[K]) => void
	onClear: () => void
	hasActiveFilters: boolean
	typeOptions: Array<{ id: string; name: string; slug: string }>
}) {
	// Any "advanced" filter active → auto-expand the advanced panel so the
	// user can see what's narrowing their list.
	const hasAdvancedActive =
		filters.priorities.size > 0 ||
		Boolean(filters.dueBefore || filters.dueAfter) ||
		Boolean(filters.approverId || filters.submitter)
	const [advancedOpen, setAdvancedOpen] = useState<boolean>(hasAdvancedActive)

	const togglePriority = (value: string) => {
		const next = new Set(filters.priorities)
		if (next.has(value)) next.delete(value)
		else next.add(value)
		onFilterChange('priorities', next)
	}

	const inputClass = 'border-input bg-background ring-offset-background flex h-10 rounded-md border px-3 py-2 text-sm'

	const setQuickDue = (preset: 'today' | '24h' | 'week' | 'overdue' | 'clear') => {
		const now = new Date()
		const iso = (d: Date) => d.toISOString()
		switch (preset) {
			case 'today': {
				const end = new Date(now)
				end.setHours(23, 59, 59, 999)
				onFilterChange('dueBefore', iso(end))
				onFilterChange('dueAfter', '')
				break
			}
			case '24h': {
				const end = new Date(now.getTime() + 24 * 60 * 60 * 1000)
				onFilterChange('dueBefore', iso(end))
				onFilterChange('dueAfter', iso(now))
				break
			}
			case 'week': {
				const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
				onFilterChange('dueBefore', iso(end))
				onFilterChange('dueAfter', iso(now))
				break
			}
			case 'overdue': {
				onFilterChange('dueBefore', iso(now))
				onFilterChange('dueAfter', '')
				break
			}
			case 'clear': {
				onFilterChange('dueBefore', '')
				onFilterChange('dueAfter', '')
				break
			}
		}
	}

	return (
		<div className="space-y-3 rounded-md border p-3">
			{/* Primary row — search / type / status. Always visible. */}
			<div className="flex flex-wrap items-center gap-2">
				<input
					type="search"
					placeholder="Search by SR number…"
					value={filters.search}
					onChange={(e) => onFilterChange('search', e.target.value)}
					className={`${inputClass} w-full max-w-xs`}
				/>
				<select
					value={filters.typeId}
					onChange={(e) => onFilterChange('typeId', e.target.value)}
					className={inputClass}
					aria-label="Request type"
				>
					<option value="">All types</option>
					{typeOptions.map((t) => (
						<option key={t.id} value={t.id}>
							{t.name}
						</option>
					))}
				</select>
				<select
					value={filters.status}
					onChange={(e) => onFilterChange('status', e.target.value as InboxFilters['status'])}
					className={inputClass}
					aria-label="Status"
				>
					<option value="pending">Pending</option>
					<option value="any">All statuses</option>
				</select>
				<button
					type="button"
					onClick={() => setAdvancedOpen((prev) => !prev)}
					aria-expanded={advancedOpen}
					aria-controls="approvals-advanced-filters"
					className="ml-auto text-xs font-medium text-muted-foreground hover:text-foreground"
				>
					{advancedOpen ? 'Hide advanced filters ▴' : 'Advanced filters ▾'}
				</button>
			</div>

			{/* Advanced panel — priority / due / approver / submitter / sort / clear.
			    Collapsed by default; expanded when any advanced filter is active. */}
			{advancedOpen ? (
				<div id="approvals-advanced-filters" className="space-y-3 border-t pt-3">
					<div className="flex flex-wrap items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground">Priority:</span>
						{PRIORITY_OPTIONS.map((p) => {
							const active = filters.priorities.has(p)
							return (
								<button
									key={p}
									type="button"
									onClick={() => togglePriority(p)}
									aria-pressed={active}
									className={`rounded-full border px-3 py-1 text-xs transition-colors ${
										active
											? 'border-primary bg-primary text-primary-foreground'
											: 'border-input text-muted-foreground hover:text-foreground'
									}`}
								>
									{p}
								</button>
							)
						})}
					</div>

					<div className="flex flex-wrap items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground">Due:</span>
						<QuickDueButton label="Today" onClick={() => setQuickDue('today')} />
						<QuickDueButton label="Next 24h" onClick={() => setQuickDue('24h')} />
						<QuickDueButton label="Next week" onClick={() => setQuickDue('week')} />
						<QuickDueButton label="Overdue" onClick={() => setQuickDue('overdue')} />
						<QuickDueButton label="Any" onClick={() => setQuickDue('clear')} />
					</div>

					<div className="flex flex-wrap items-center gap-2">
						<input
							type="text"
							placeholder="Approver UUID…"
							value={filters.approverId}
							onChange={(e) => onFilterChange('approverId', e.target.value)}
							className={`${inputClass} max-w-xs`}
							aria-label="Approver UUID"
						/>
						<input
							type="text"
							placeholder="Submitter UUID…"
							value={filters.submitter}
							onChange={(e) => onFilterChange('submitter', e.target.value)}
							className={`${inputClass} max-w-xs`}
							aria-label="Submitter UUID"
						/>
					</div>

					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="flex items-center gap-2">
							<span className="text-xs font-medium text-muted-foreground">Sort:</span>
							<select
								value={filters.sort}
								onChange={(e) => onFilterChange('sort', e.target.value as InboxFilters['sort'])}
								className={inputClass}
								aria-label="Sort column"
							>
								{SORT_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
							<select
								value={filters.order}
								onChange={(e) => onFilterChange('order', e.target.value as InboxFilters['order'])}
								className={inputClass}
								aria-label="Sort order"
							>
								<option value="asc">Ascending</option>
								<option value="desc">Descending</option>
							</select>
						</div>
						{hasActiveFilters ? (
							<Button variant="outline" size="sm" onClick={onClear}>
								Clear filters
							</Button>
						) : null}
					</div>
				</div>
			) : null}
		</div>
	)
}

function QuickDueButton({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="rounded-full border border-input px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
		>
			{label}
		</button>
	)
}

/**
 * Top-level view tabs for admin-scope switching. Rendered above the filter
 * bar (not inside it) so the two views — "assigned to me" vs "all approvals"
 * — are first-class concepts, not filter options. Non-admin users don't see
 * the tabs (they're always clamped to `mine`).
 */
function ScopeTabs({ scope, onChange }: { scope: 'mine' | 'all'; onChange: (next: 'mine' | 'all') => void }) {
	return (
		<nav role="tablist" aria-label="Approvals scope" className="flex gap-1 border-b">
			<ScopeTab label="Assigned to me" active={scope === 'mine'} onClick={() => onChange('mine')} />
			<ScopeTab label="All approvals" active={scope === 'all'} onClick={() => onChange('all')} />
		</nav>
	)
}

function ScopeTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			role="tab"
			aria-selected={active}
			onClick={onClick}
			className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
				active
					? 'border-primary text-foreground'
					: 'border-transparent text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground'
			}`}
		>
			{label}
		</button>
	)
}

/**
 * Partitions a page of admin-scope=all rows by `viewerMatch`: rows the viewer
 * is personally assigned to (direct / manager / group) go into "Assigned to
 * me"; everything else goes into "Other approvers". Both sections render even
 * when empty so the admin gets signal that their personal queue is clear.
 *
 * Pagination is global (`data.total` across both groups), so this is purely
 * a visual split of the current page.
 */
function GroupedList({
	rows,
	selected,
	onToggleSelected,
}: {
	rows: ApprovalDTO[]
	selected: Set<string>
	onToggleSelected: (id: string) => void
}) {
	const mine: ApprovalDTO[] = []
	const others: ApprovalDTO[] = []
	for (const r of rows) {
		if (r.viewerMatch !== null) mine.push(r)
		else others.push(r)
	}
	// Float "submitter replied" pending rows to the top of "Assigned to me"
	// so the approver sees them first. Stable within each subgroup.
	mine.sort((a, b) => priorityForAnswered(b) - priorityForAnswered(a))
	return (
		<div className="space-y-4" data-testid="approvals-inbox-list">
			<section aria-label="Assigned to me" className="space-y-2">
				<h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					Assigned to me · {mine.length}
				</h2>
				{mine.length === 0 ? (
					<p className="rounded-md border p-4 text-xs text-muted-foreground">No approvals in your personal queue.</p>
				) : (
					<ul className="space-y-2">
						{mine.map((a) => (
							<ApprovalRow
								key={a.id}
								approval={a}
								isAdmin
								selected={selected.has(a.id)}
								onToggleSelected={onToggleSelected}
							/>
						))}
					</ul>
				)}
			</section>
			<section aria-label="Other approvers" className="space-y-2">
				<h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					Other approvers · {others.length}
				</h2>
				{others.length === 0 ? (
					<p className="rounded-md border p-4 text-xs text-muted-foreground">No other approvals on this page.</p>
				) : (
					<ul className="space-y-2">
						{others.map((a) => (
							<ApprovalRow
								key={a.id}
								approval={a}
								isAdmin
								selected={selected.has(a.id)}
								onToggleSelected={onToggleSelected}
							/>
						))}
					</ul>
				)}
			</section>
		</div>
	)
}

/**
 * Compute the SLA urgency bucket from `dueAt` and the row status.
 *
 *   - status !== `pending`    → `decided` (no SLA — the queue is closed,
 *                               showing "Overdue" on an already-approved or
 *                               rejected row is misleading)
 *   - `dueAt` null            → `unknown` (grey "No SLA" pill)
 *   - past due (still pending) → `overdue` (red)
 *   - within 4 hours          → `soon` (amber)
 *   - otherwise               → `on-track` (green)
 */
type UrgencyBucket = 'unknown' | 'overdue' | 'soon' | 'on-track' | 'decided'

function urgencyBucket(dueAt: string | null, status: string): UrgencyBucket {
	if (status !== 'pending') return 'decided'
	if (!dueAt) return 'unknown'
	const due = new Date(dueAt).getTime()
	const now = Date.now()
	if (due < now) return 'overdue'
	if (due - now <= 4 * 60 * 60 * 1000) return 'soon'
	return 'on-track'
}

const URGENCY_STYLES: Record<UrgencyBucket, { border: string; pillLabel: string | null; pillClass: string }> = {
	unknown: { border: 'border-l-muted-foreground/30', pillLabel: 'No SLA', pillClass: 'bg-muted text-muted-foreground' },
	'on-track': {
		border: 'border-l-emerald-500',
		pillLabel: 'On track',
		pillClass: 'bg-emerald-50 text-emerald-700',
	},
	soon: { border: 'border-l-amber-500', pillLabel: 'Due soon', pillClass: 'bg-amber-50 text-amber-700' },
	overdue: { border: 'border-l-rose-500', pillLabel: 'Overdue', pillClass: 'bg-rose-50 text-rose-700' },
	// Decided rows: muted left border + no SLA pill (the row's own status
	// badge on the right already conveys the outcome).
	decided: { border: 'border-l-muted-foreground/30', pillLabel: null, pillClass: '' },
}

/**
 * Humanise `viewerMatch` into the "Why is this in my queue?" line for
 * non-admin viewers. `null` (the admin case for a row they don't own)
 * returns `null` and the row's explainer line is skipped.
 */
function viewerMatchExplainer(match: ApprovalDTO['viewerMatch']): string | null {
	switch (match) {
		case 'direct':
			return 'Directly assigned to you'
		case 'manager':
			return 'You are the resolved manager for this request'
		case 'group':
			return 'You are a member of an approver group'
		default:
			return null
	}
}

function formatRelative(iso: string): string {
	const diffMs = Date.now() - new Date(iso).getTime()
	const minutes = Math.round(diffMs / 60000)
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.round(minutes / 60)
	if (hours < 48) return `${hours}h ago`
	const days = Math.round(hours / 24)
	return `${days}d ago`
}

function ApprovalRow({
	approval,
	isAdmin,
	selected,
	onToggleSelected,
}: {
	approval: ApprovalDTO
	isAdmin: boolean
	selected: boolean
	onToggleSelected: (id: string) => void
}) {
	const stepLabel = approval.stepLabel ?? approval.stepName
	const assignedTo = resolveAssignedTo(approval)
	const urgency = urgencyBucket(approval.dueAt, approval.status)
	const urgencyStyle = URGENCY_STYLES[urgency]
	const explainer = !isAdmin ? viewerMatchExplainer(approval.viewerMatch) : null
	// Only pending rows are selectable — decided ones have nothing to bulk-act on.
	const selectable = approval.status === 'pending'
	// `request.title` is the first form-field value; when the first field is
	// a UUID-valued picker (user / project / autocomplete) the value renders
	// as a raw id which is useless on the card. Hide those — the other lines
	// (type, SR number, step, assignee, submitter) already give full context.
	const showTitle = approval.request.title && !isLikelyUuid(approval.request.title)

	return (
		<li className="flex items-start gap-2">
			<input
				type="checkbox"
				aria-label={`Select approval ${approval.request.requestNumber}`}
				checked={selected}
				disabled={!selectable}
				onChange={() => onToggleSelected(approval.id)}
				className="mt-5 h-4 w-4 shrink-0 rounded border-input"
			/>
			<Link to={`/requests-center/approvals/${approval.id}`} className="block flex-1 focus:outline-none">
				<Card
					className={`flex items-start justify-between gap-3 border-l-4 p-4 transition-colors hover:border-primary/50 focus-visible:border-primary ${urgencyStyle.border}`}
				>
					<div className="min-w-0 flex-1 space-y-1">
						<div className="flex flex-wrap items-center gap-2">
							<span className="font-medium text-sm">{approval.request.typeName}</span>
							<span className="text-xs text-muted-foreground">{approval.request.requestNumber}</span>
							<Badge variant="outline">{approval.request.priority}</Badge>
							{urgencyStyle.pillLabel ? (
								<span className={`rounded px-2 py-0.5 text-xs ${urgencyStyle.pillClass}`}>
									{urgencyStyle.pillLabel}
								</span>
							) : null}
							{approval.status === 'pending' && approval.answeredInfoGate ? (
								<SubmitterRepliedPill
									completedAt={approval.answeredInfoGate.completedAt}
									dataTestId={`submitter-replied-pill-${approval.id}`}
								/>
							) : null}
						</div>
						{showTitle ? <p className="truncate text-sm">{approval.request.title}</p> : null}
						{approval.status === 'pending' && approval.answeredInfoGate ? (
							<p
								className="truncate text-xs font-medium text-teal-700"
								data-testid={`submitter-replied-preview-${approval.id}`}
							>
								New info: {approval.answeredInfoGate.providedDataKeys.join(', ') || '—'} · click to review
							</p>
						) : null}
						<p className="text-xs text-muted-foreground">
							Submitted by {approval.request.submitterDisplayName} · {formatRelative(approval.createdAt)}
							{approval.dueAt ? ` · Due ${new Date(approval.dueAt).toLocaleString()}` : ''}
						</p>
						<p className="text-xs text-muted-foreground">
							Step {approval.stepOrder}/{approval.totalSteps} · {stepLabel}
						</p>
						{assignedTo ? <p className="text-xs text-muted-foreground">Assigned to: {assignedTo}</p> : null}
						{explainer ? <p className="text-xs font-medium text-primary">{explainer}</p> : null}
					</div>
					<Badge>{approval.status}</Badge>
				</Card>
			</Link>
		</li>
	)
}

/**
 * Sort key for "submitter replied" priority. Pending + answered = top
 * (priority 2); pending only = 1; decided = 0. Combined with `Array.sort`'s
 * stable behaviour, this preserves the inbox's underlying order within each
 * tier so date-driven sorts still apply secondarily.
 */
function priorityForAnswered(a: ApprovalDTO): number {
	if (a.status !== 'pending') return 0
	return a.answeredInfoGate ? 2 : 1
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isLikelyUuid(s: string): boolean {
	return UUID_RE.test(s.trim())
}

/**
 * Flattens the three approver dimensions (direct approvers, resolved manager,
 * approver groups) into a single readable line for the card.
 */
function resolveAssignedTo(approval: ApprovalDTO): string | null {
	const parts: string[] = []
	if (approval.approverDisplayNames.length > 0) parts.push(...approval.approverDisplayNames)
	if (approval.resolvedManagerDisplayName) parts.push(approval.resolvedManagerDisplayName)
	if (approval.approverGroupNames.length > 0) parts.push(...approval.approverGroupNames)
	return parts.length > 0 ? parts.join(', ') : null
}

function EmptyState({ variant, onClear }: { variant: 'empty' | 'filtered'; onClear?: () => void }) {
	if (variant === 'filtered') {
		return (
			<div className="rounded-md border p-8 text-center space-y-2">
				<p className="text-sm text-muted-foreground">No approvals match your filters.</p>
				{onClear ? (
					<Button variant="outline" size="sm" onClick={onClear}>
						Clear filters
					</Button>
				) : null}
			</div>
		)
	}
	return (
		<div className="rounded-md border p-8 text-center">
			<p className="text-sm text-muted-foreground">You&rsquo;re all caught up.</p>
		</div>
	)
}

function ListSkeleton() {
	return (
		<ul className="space-y-2">
			{[0, 1, 2].map((i) => (
				<li key={i} className="h-16 animate-pulse rounded-md border bg-muted/40" />
			))}
		</ul>
	)
}

/**
 * Bulk action toolbar. Rendered above the list whenever there's at least one
 * row visible. The select-all checkbox reflects the pending rows on the
 * current page; the Approve / Reject / Clear actions are only enabled when
 * something is selected.
 */
function BulkToolbar({
	selectedCount,
	selectableCount,
	allSelected,
	onToggleAll,
	onApprove,
	onReject,
	onClearSelection,
}: {
	selectedCount: number
	selectableCount: number
	allSelected: boolean
	onToggleAll: () => void
	onApprove: () => void
	onReject: () => void
	onClearSelection: () => void
}) {
	return (
		<div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
			<label className="flex items-center gap-2 text-xs">
				<input
					type="checkbox"
					aria-label={allSelected ? 'Deselect all pending on this page' : 'Select all pending on this page'}
					checked={allSelected}
					disabled={selectableCount === 0}
					onChange={onToggleAll}
					className="h-4 w-4 rounded border-input"
				/>
				<span className="text-muted-foreground">
					{selectedCount > 0
						? `${selectedCount} selected`
						: selectableCount === 0
							? 'No pending rows to select'
							: 'Select all on page'}
				</span>
			</label>
			<div className="ml-auto flex gap-2">
				<Button variant="outline" size="sm" onClick={onApprove} disabled={selectedCount === 0}>
					Approve
				</Button>
				<Button variant="destructive" size="sm" onClick={onReject} disabled={selectedCount === 0}>
					Reject
				</Button>
				<Button variant="ghost" size="sm" onClick={onClearSelection} disabled={selectedCount === 0}>
					Clear
				</Button>
			</div>
		</div>
	)
}

/**
 * Summary banner shown after a bulk action completes. Stays visible until
 * the user dismisses it; errors expand inline so the user can see which ids
 * failed and why (concurrent 409, stale row, permission, etc.).
 */
function BulkSummaryBanner({ summary, onDismiss }: { summary: BulkSummary; onDismiss: () => void }) {
	const verb = summary.action === 'approve' ? 'Approved' : 'Rejected'
	const anyFailures = summary.failures.length > 0
	return (
		<div
			role="status"
			className={`space-y-2 rounded-md border p-3 text-sm ${
				anyFailures
					? 'border-amber-400 bg-amber-50 text-amber-900'
					: 'border-emerald-400 bg-emerald-50 text-emerald-900'
			}`}
		>
			<div className="flex items-start justify-between gap-2">
				<p>
					<strong>{verb}</strong> {summary.succeeded} of {summary.total}
					{anyFailures ? ` · ${summary.failures.length} failed` : ''}.
				</p>
				<button type="button" onClick={onDismiss} aria-label="Dismiss" className="text-xs font-medium underline">
					Dismiss
				</button>
			</div>
			{anyFailures ? (
				<ul className="space-y-1 text-xs">
					{summary.failures.map((f) => (
						<li key={f.id} className="font-mono">
							{f.id.slice(0, 8)}… — {f.message}
						</li>
					))}
				</ul>
			) : null}
		</div>
	)
}

/**
 * Modal for collecting the comment before a bulk action runs. Approve
 * accepts an optional comment; reject requires a non-empty one — matches
 * the single-row action rules.
 *
 * No portal / backdrop abstraction used — matches the lightweight
 * inline modal pattern used on the single-row detail page.
 */
function BulkActionModal({
	action,
	targetCount,
	running,
	onCancel,
	onConfirm,
}: {
	action: 'approve' | 'reject'
	targetCount: number
	running: boolean
	onCancel: () => void
	onConfirm: (comment: string) => void
}) {
	const [comment, setComment] = useState('')
	const needsComment = action === 'reject'
	const disabled = running || (needsComment && comment.trim().length === 0)
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
			role="dialog"
			aria-modal="true"
			aria-label={`Bulk ${action}`}
		>
			<div className="w-full max-w-md space-y-3 rounded-md border bg-background p-4 shadow-lg">
				<h2 className="text-lg font-semibold">
					{action === 'approve' ? 'Bulk approve' : 'Bulk reject'} — {targetCount} approval
					{targetCount === 1 ? '' : 's'}
				</h2>
				<label className="block text-sm font-medium" htmlFor="bulk-comment">
					Comment{' '}
					<span className="text-xs font-normal text-muted-foreground">
						{needsComment ? '(required)' : '(optional)'}
					</span>
				</label>
				<textarea
					id="bulk-comment"
					className="border-input bg-background ring-offset-background flex min-h-[90px] w-full rounded-md border px-3 py-2 text-sm"
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder={
						action === 'approve'
							? 'Add context for the submitters (optional)…'
							: 'Explain why these approvals are being rejected…'
					}
					disabled={running}
				/>
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onCancel} disabled={running}>
						Cancel
					</Button>
					<Button
						variant={action === 'reject' ? 'destructive' : 'default'}
						onClick={() => onConfirm(comment)}
						disabled={disabled}
					>
						{running ? `${action === 'approve' ? 'Approving' : 'Rejecting'}…` : `Confirm ${action}`}
					</Button>
				</div>
			</div>
		</div>
	)
}
